import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import LinkTabs from 'components/UI/LinkTabs/LinkTabs'
import { usePageTitle } from 'context/pageTitleContext'
import CreatorsList from 'features/CreatorsList/CreatorsList'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { AllIcons } from 'helpers/allIcons'
import { useFilterQuery } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getSubscriptions as fetchSubscriptions } from 'services/endpoints/subscriptions'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useSubscriptionCacheStore } from 'store/subscriptionCacheStore'
import './_subscriptions.scss'

const useSortSubsActive = () => {
  const { t } = useTranslation()
  return [
    { value: 1, name: t('recentlySubscribed') },
    { value: 3, name: t('expiring') },
    { value: 4, name: t('onlineStatus') },
    { value: 5, name: t('lastActivity') },
    { value: 6, name: t('name') }
  ]
}

const useSortSubsExpired = () => {
  const { t } = useTranslation()
  return [
    { value: 1, name: t('recentlyExpired') },
    { value: 2, name: t('price') },
    { value: 3, name: t('discount') },
    { value: 4, name: t('name') }
  ]
}

const useSubsOrder = () => {
  const { t } = useTranslation()
  return [
    { value: 1, name: t('ascending') },
    { value: 2, name: t('descending') }
  ]
}

export const Subscriptions = () => {
  const navigate = useNavigate()
  const { setTitle } = usePageTitle()
  const sortSubsActive = useSortSubsActive()
  const sortSubsExpired = useSortSubsExpired()
  const sortSubsOrder = useSubsOrder()
  const { t } = useTranslation()

  const filter = useFilterQuery()
  const typeQS = filter.get('type')
  const sortQS = filter.get('sort')
  const orderQS = filter.get('order')

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedSort, setSelectedSort] = useState(1)
  const [selectedOrder, setSelectedOrder] = useState(2)
  const [enabledQuery, setEnabledQuery] = useState(false)

  const { getSubscriptions, setSubscriptions } = useSubscriptionCacheStore()
  const { setProfile } = useProfileCacheStore()

  const tabType: 'active' | 'expired' = typeQS === 'expired' ? 'expired' : 'active'
  const [activeFilter, setActiveFilter] = useState<'active' | 'expired'>(tabType)

  const cachedInitial = getSubscriptions(tabType)
  const [creators, setCreators] = useState<any[]>(cachedInitial ?? [])

  const {
    data,
    isFetching,
    isLoading,
    isFetched
  } = useInfiniteQuery(
    [
      'subscriptions',
      activeFilter,
      selectedOrder,
      selectedSort,
      debouncedSearchTerm
    ],
    async ({ pageParam = 1 }) =>
      await fetchSubscriptions(
        selectedSort,
        activeFilter,
        selectedOrder,
        pageParam,
        debouncedSearchTerm
      ),
    {
      getNextPageParam: lastPage => lastPage?.nextCursor,
      enabled: enabledQuery,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 120_000
    }
  )

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 300)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [searchTerm])

  useEffect(() => {
    setTitle('Subscriptions')
  }, [setTitle])

  useEffect(() => {
    if (sortQS) setSelectedSort(parseInt(sortQS))
    if (orderQS) setSelectedOrder(parseInt(orderQS))
  }, [sortQS, orderQS])

  useEffect(() => {
    setActiveFilter(tabType)

    const cached = getSubscriptions(tabType)
    setCreators(cached ?? [])

    setEnabledQuery(true)
  }, [tabType, getSubscriptions])

  useEffect(() => {
    if (data && !isFetching) {
      const tmp = data.pages
        .map(page => page?.page.data.data)
        .flat()
        .map(item => {
          const model = item.model_data
          const profile = {
            username: model.username,
            avatar: model.avatar,
            name: `${model.first_name} ${model.last_name}`,
            isOnline: model.is_online,
            isLive: model.active_stream,
            userId: model.user_id,
            ...model
          }

          setProfile(model.username, profile)

          return {
            coverUrl: model.cover,
            avatarUrl: model.avatar,
            name: model.name,
            handle: model.username,
            isLive: model.active_stream,
            isOnline: model.is_online,
            conversation_id: model.conversation_id,
            isActive: activeFilter === 'active',
            price: model.price,
            current_plan_price: model.current_plan_price,
            save: model.discount,
            userId: model.user_id,

            subscriptionPlans: model.subscription_plans ?? [],
            promoCampaign: model.promo_campaign ?? null,

            newPostsCount: model.new_posts,
            expires: model.expires,
            billingCycle: model.billing_cycle,
            monthsSubscribed: model.total_months_subscribed,
            renewalPrice: model.one_month_price,
            renewDate:
              model.subscription_renew_date === 'no renewal'
                ? ''
                : model.subscription_renew_date,
            lastActivity: model.last_activity
          }
        })

      setCreators(tmp)
      if (!debouncedSearchTerm) {
        setSubscriptions(activeFilter, tmp)
      }
    }
  }, [data, isFetching, activeFilter, setProfile, setSubscriptions])

  const showInitialLoader =
    enabledQuery &&
    creators.length === 0 &&
    !isFetched &&
    isLoading

  return (
    <WithHeaderSection
      customClass='subscriptions-whs'
      headerSection={
        <LayoutHeader
          type='search-with-buttons'
          searchValue={searchTerm}
          searchFn={setSearchTerm}
          clearFn={() => setSearchTerm('')}
          additionalProps={{ placeholder: t('searchSubscriptions') }}
          buttons={[
            {
              type: 'cta',
              icon: AllIcons.list,
              action: () => navigate('/subscriptions/lists')
            },
            {
              type: 'sort',
              elements: {
                first_section: activeFilter === 'active' ? sortSubsActive : sortSubsExpired,
                second_section: sortSubsOrder
              },
              selectedSort: { selectedSort },
              selectedOrder: { selectedOrder },
              activeFilter: { activeFilter }
            }
          ]}
          applyFn={(val: string, val1?: string) => {
            if (val) setSelectedSort(parseInt(val))
            if (val1) setSelectedOrder(parseInt(val1))
          }}
        />
      }
    >
      <div className='subscriptions__tabs'>
        <LinkTabs
          route='/subscriptions'
          filters={['active', 'expired']}
          activeFilter={activeFilter}
          compact
        />
        <div className='subscriptions__tabs--count'>
          <span>{creators.length} </span>
          {t('subscriptions')}
        </div>
      </div>

      <CreatorsList
        creators={creators}
        heightAdjustment={320}
        renderLocation={
          activeFilter === 'active' ? 'subscriptionsActive' : 'subscriptionsExpired'
        }
        searchTerm={searchTerm}
        isLoading={showInitialLoader}
      />

{/*       {activeFilter === 'active' && (
        <div>
          <div className='subscriptions__divider'></div>
        </div>
      )} */}
    </WithHeaderSection>
  )
}